import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, MapPin, CalendarDays, ArrowRight, Users, Compass, Coffee } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';
import EventRatingBadge from '@/components/EventRatingBadge';
import TestimonialsCarousel from '@/components/TestimonialsCarousel';
import { isPastEvent } from '@/utils/eventCountdown';

const HomePage = () => {
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  // Bundle 164 D - homepage Featured section Upcoming/Past
  // tabs. Ephemeral (no URL persistence, unlike /events).
  // Counts + split use the shared `isPastEvent()` helper.
  const [featuredTab, setFeaturedTab] = useState('upcoming');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // Fetch upcoming + past in parallel so both tabs have
        // real data without a second network call on tab switch.
        // Past uses the Bundle 155 `past_only=true` filter.
        const [up, pa] = await Promise.all([
          api.get('/events'),
          api.get('/events?past_only=true&limit=12'),
        ]);
        if (!cancel) {
          const upcoming = (up.data.events || [])
            .filter((e) => !isPastEvent(e));
          const past = pa.data.events || [];
          setEvents({ upcoming, past });
        }
      } catch (e) {
        // silent - homepage is still useful without events
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const upcomingList = useMemo(
    () => (events.upcoming || []).slice(0, 3), [events.upcoming]);
  const pastList = useMemo(
    () => (events.past || []).slice(0, 3), [events.past]);
  const activeList = featuredTab === 'past' ? pastList : upcomingList;
  const upcomingCount = (events.upcoming || []).length;
  const pastCount = (events.past || []).length;

  return (
    <div>
      {/* HERO - Dostination signature dark+ember gradient (see design_language.md §1.1) */}
      <section className="relative overflow-hidden dost-signature-bg">
        {/* Signature gold accent line at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary-500 to-transparent"></div>

        <div className="relative container mx-auto px-4 md:px-8 py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-secondary-400 text-xs font-semibold uppercase tracking-widest mb-6">
            <Sparkles className="h-3.5 w-3.5 text-secondary-500" /> Curated with care
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Where thoughtful people <br className="hidden md:block" />find their <span className="text-secondary-500 italic">dost</span>.
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-primary-100 leading-relaxed mb-10">
            Premium, warm, cultured, intentional gatherings and travel experiences  - 
            designed for real conversation and warm introductions, not swipes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-primary-900 font-semibold shadow-gold hover:from-secondary-600 hover:to-secondary-700">
              <Link to="/events">
                Explore events
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-secondary-500/60 text-secondary-200 bg-white/5 hover:bg-secondary-500/10 hover:text-white hover:border-secondary-500">
              <Link to="/auth/register">Join Dostination</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="container mx-auto px-4 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-3">Our promise</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-4">
            A different kind of gathering
          </h2>
          <p className="text-neutral-600 leading-relaxed">
            Every Dostination event - from a Dallas lounge night to a private retreat - is designed
            around a few simple ideas.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: Coffee,
              title: 'Cultured & warm',
              body: 'Rooted in South-Asian hospitality and adjacent cultures - food, music, and slow conversation take center stage.',
            },
            {
              icon: Compass,
              title: 'Intentional, not swipe-y',
              body: 'No matching percentages, no swipe cards. Just shared context and suggested introductions when both sides say yes.',
            },
            {
              icon: Users,
              title: 'Curated by intent, not by count',
              body: 'Small isn\'t the point. Care is. Every guest is invited or vetted, and every room is composed with a considered mix so the conversation carries itself.',
            },
          ].map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border shadow-soft hover:shadow-gold transition-all duration-300">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-secondary-700" />
                </div>
                <h3 className="font-display text-xl font-semibold text-primary-900 mb-2">{title}</h3>
                <p className="text-neutral-600 leading-relaxed text-sm">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Bundle 80.2 / Bundle 164 B - homepage carousel uses
          the `single` variant (1-at-a-time, 7s auto-advance,
          reduced-motion respected). Hits the Bundle 164
          endpoint which enforces stricter source rules on top
          of the Bundle 80.2 published-only filter. */}
      <TestimonialsCarousel
        variant="single"
        endpoint="/testimonials/carousel"
        params={{ limit: 10 }}
        fallbackEndpoint={null}
        fallbackParams={null}
        eyebrow="Real stories, real Dostinations"
        headline="What our guests remember."
      />

      {/* FEATURED EVENTS - Bundle 164 D adds Upcoming / Past
          sub-tabs (ephemeral, no URL persistence). Counts +
          split use the shared `isPastEvent()` helper. */}
      <section className="bg-neutral-50 border-y border-neutral-100">
        <div className="container mx-auto px-4 md:px-8 py-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-2">Featured</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900">
                {featuredTab === 'past' ? 'Past events' : 'Upcoming events'}
              </h2>
            </div>
            <Link
              to={featuredTab === 'past'
                ? '/events?tab=past' : '/events?tab=upcoming'}
              className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-primary-800 hover:text-primary-900"
              data-testid="home-featured-view-all"
            >
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div
            className="flex items-center gap-2 mb-8"
            role="tablist"
            aria-label="Upcoming or past events"
            data-testid="home-featured-tabs"
          >
            {[
              { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
              { key: 'past', label: 'Past', count: pastCount },
            ].map((t) => {
              const active = featuredTab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFeaturedTab(t.key)}
                  className={
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs '
                    + (active
                      ? 'bg-primary-900 text-white border-primary-900'
                      : 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400')
                  }
                  data-testid={`home-featured-tab-${t.key}`}
                >
                  <span>{t.label}</span>
                  <span
                    className={
                      'rounded-full text-[10px] px-1.5 py-0.5 '
                      + (active
                        ? 'bg-white/20 text-white'
                        : 'bg-neutral-100 text-neutral-700')
                    }
                    data-testid={`home-featured-tab-${t.key}-count`}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" />
            </div>
          ) : activeList.length === 0 ? (
            <div
              className="text-neutral-500 text-sm text-center py-8"
              data-testid={`home-featured-empty-${featuredTab}`}
            >
              {featuredTab === 'past'
                ? 'No past events yet.'
                : 'No upcoming events scheduled.'}
            </div>
          ) : (
            <div
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
              data-testid={`home-featured-grid-${featuredTab}`}
            >
              {activeList.map((e) => (
                <EventCardPublic key={e.id} event={e} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const EventCardPublic = ({ event }) => {
  const { trackEvent } = usePixels();
  const date = event.start_datetime || event.start_date;
  const dateLabel = date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD';
  const city = event.city || event.location?.city;
  const country = event.country || event.location?.country;
  const locLabel = [city, country].filter(Boolean).join(', ') || ' - ';

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group"
      onClick={() => trackEvent('Clicked on Event Card', {
        content_name: event.title,
        content_ids: [event.slug || event.id],
      })}
    >
      <Card className="border shadow-soft group-hover:shadow-gold transition-all duration-300 h-full overflow-hidden">
        <div className="aspect-[16/9] bg-gradient-to-br from-primary-600 to-primary-900 relative overflow-hidden">
          {(event.hero_image_list_url || event.hero_image_url) ? (
            <img src={event.hero_image_list_url || event.hero_image_url} alt={event.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-secondary-300">
              <Sparkles className="h-10 w-10" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-primary-900 text-xs font-semibold">
              <CalendarDays className="h-3 w-3" /> {dateLabel}
            </span>
          </div>
        </div>
        <CardContent className="p-5">
          <h3 className="font-display text-xl font-semibold text-primary-900 mb-1 line-clamp-1">{event.title}</h3>
          {event.tagline && <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mb-3">{event.tagline}</p>}
          <div className="flex items-center gap-1 text-xs text-neutral-500">
            <MapPin className="h-3.5 w-3.5 text-secondary-600" /> {locLabel}
          </div>
          {/* Bundle 77 - role-aware rating badge; renders nothing when hidden. */}
          <div className="mt-2">
            <EventRatingBadge eventId={event.id} />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default HomePage;
